export interface ChunkedFileUploadOptions {
  extraFields?: Record<string, string>;
}

export interface ChunkedFileUploadResponse {
  fieldId: string;
  fileName: string;
  success: boolean;
  complete?: boolean;
  error?: string;
}

export async function chunkedFileUpload(file: File,
                                        url: string,
                                        onProgress: (progress: number) => void,
                                        fileNameOverride?: string,
                                        options?: ChunkedFileUploadOptions
): Promise<ChunkedFileUploadResponse | void> {
  const chunkSize: number = 10 * 1024 * 1024; // 10MB chunks
  const totalChunks: number = Math.ceil(file.size / chunkSize);
  let chunkNumber: number = 0;
  let bytesUploaded: number = 0;
  let lastResponse: ChunkedFileUploadResponse | undefined;

  while (chunkNumber < totalChunks) {
    const start: number = chunkNumber * chunkSize;
    const end: number = Math.min(start + chunkSize, file.size);
    const chunk: Blob = file.slice(start, end);

    const formData: FormData = new FormData();
    formData.append('chunk', chunk);
    formData.append('chunkNumber', chunkNumber.toString());
    formData.append('totalChunks', totalChunks.toString());
    formData.append('fileName', fileNameOverride ?? file.name);

    // Append extra fields if provided
    if (options?.extraFields) {
      for (const [key, value] of Object.entries(options.extraFields)) {
        formData.append(key, value);
      }
    }

    try {
      lastResponse = await new Promise<ChunkedFileUploadResponse | undefined>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);

        // Track upload progress for this chunk
        xhr.upload.onprogress = (event: ProgressEvent) => {
          if (event.lengthComputable) {
            // Calculate total bytes uploaded (previous chunks + current chunk progress)
            const currentChunkProgress = event.loaded;
            const totalBytesUploaded = bytesUploaded + currentChunkProgress;
            const totalProgress = (totalBytesUploaded / file.size) * 100;
            onProgress(Math.min(totalProgress, 100)); // Cap at 100%
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            bytesUploaded += chunk.size; // Update total bytes after chunk completes
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response as ChunkedFileUploadResponse);
            } catch {
              resolve(undefined);
            }
          } else {
            try {
              const errorResponse = JSON.parse(xhr.responseText);
              reject(new Error(errorResponse.error || `Chunk upload failed: ${xhr.statusText}`));
            } catch {
              reject(new Error(`Chunk upload failed: ${xhr.statusText}`));
            }
          }
        };

        xhr.onerror = () => reject(new Error('Network error during chunk upload'));

        xhr.send(formData);
      });

      chunkNumber++;
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    }
  }

  return lastResponse;
}
